<template>
  <nav class="erp-sidebar" :class="{ collapsed }">

    <!-- Brand / Logo -->
    <div class="sidebar-brand">
      <div class="brand-icon">
        <i class="bi bi-box-seam-fill"></i>
      </div>
      <span class="brand-text">{{ company.name }}</span>
    </div>

    <!-- Toggle button -->
    <button class="sidebar-toggle" @click="collapsed = !collapsed" :title="collapsed ? 'Expand' : 'Collapse'">
      <i :class="collapsed ? 'bi bi-chevron-right' : 'bi bi-chevron-left'"></i>
    </button>

    <!-- Navigation -->
    <div class="sidebar-nav">

      <!-- OVERVIEW -->
      <div class="nav-section">
        <span class="section-label">Overview</span>
        <router-link to="/" class="nav-link" active-class="active" exact>
          <i class="bi bi-speedometer2"></i>
          <span class="nav-text">{{ $t('nav.dashboard') }}</span>
        </router-link>
        <router-link to="/ai-history" class="nav-link" active-class="active">
          <i class="bi bi-clock-history"></i>
          <span class="nav-text">AI History</span>
        </router-link>
        <router-link to="/reports" class="nav-link" active-class="active">
          <i class="bi bi-graph-up-arrow"></i>
          <span class="nav-text">{{ $t('nav.reports') }}</span>
        </router-link>
      </div>

      <!-- INVENTORY -->
      <div class="nav-section">
        <span class="section-label">Inventory</span>
        <router-link to="/products" class="nav-link" active-class="active">
          <i class="bi bi-box-seam"></i>
          <span class="nav-text">{{ $t('nav.products') }}</span>
        </router-link>
        <router-link to="/categories" class="nav-link" active-class="active">
          <i class="bi bi-tags-fill"></i>
          <span class="nav-text">{{ $t('nav.categories') }}</span>
        </router-link>
        <router-link to="/stock" class="nav-link" active-class="active">
          <i class="bi bi-archive-fill"></i>
          <span class="nav-text">{{ $t('nav.stock') }}</span>
        </router-link>
        <router-link to="/warehouses" class="nav-link" active-class="active">
          <i class="bi bi-building-fill"></i>
          <span class="nav-text">{{ $t('nav.warehouses') }}</span>
        </router-link>
      </div>

      <!-- OPERATIONS -->
      <div class="nav-section">
        <span class="section-label">Operations</span>
        <router-link to="/purchase-orders" class="nav-link" active-class="active">
          <i class="bi bi-bag-plus-fill"></i>
          <span class="nav-text">{{ $t('nav.purchase') }}</span>
        </router-link>
        <router-link to="/sales-orders" class="nav-link" active-class="active">
          <i class="bi bi-cart-fill"></i>
          <span class="nav-text">{{ $t('nav.sales') }}</span>
        </router-link>
      </div>

      <!-- CONTACTS -->
      <div class="nav-section">
        <span class="section-label">Contacts</span>
        <router-link to="/suppliers" class="nav-link" active-class="active">
          <i class="bi bi-truck-front-fill"></i>
          <span class="nav-text">{{ $t('nav.suppliers') }}</span>
        </router-link>
        <router-link to="/customers" class="nav-link" active-class="active">
          <i class="bi bi-people-fill"></i>
          <span class="nav-text">{{ $t('nav.customers') }}</span>
        </router-link>
      </div>

      <!-- ADMINISTRATION -->
      <div v-if="userRole === 'Admin'" class="nav-section">
        <span class="section-label">Administration</span>
        <router-link to="/users" class="nav-link" active-class="active">
          <i class="bi bi-people-fill"></i>
          <span class="nav-text">{{ $t('nav.users') }}</span>
        </router-link>
      </div>

    </div>

    <!-- Bottom User Info -->
    <div class="sidebar-footer">
      <div class="user-avatar">
        <i class="bi bi-person-fill"></i>
      </div>
      <div class="user-info">
        <span class="user-name">{{ username }}</span>
        <span class="user-role">{{ userRole }}</span>
      </div>
    </div>

  </nav>
</template>

<script setup>
import { ref, onMounted } from 'vue'

import { company } from '../api/axios'

const collapsed = ref(false)
const username = ref('Admin')
const userRole = ref('System User')

onMounted(() => {
  username.value = localStorage.getItem('username') || 'Admin'
  userRole.value = localStorage.getItem('user_role') || 'System User'
})
</script>

<style scoped>
/* ── Variables ── */
.erp-sidebar {
  --sidebar-width: 240px;
  --sidebar-collapsed: 64px;
  --brand-height: 64px;
  --bg: #0f172a;
  --bg-hover: #1e293b;
  --bg-active: #1d4ed8;
  --accent: #3b82f6;
  --text: #94a3b8;
  --text-bright: #f1f5f9;
  --section-label-color: #475569;
  --border: #1e293b;
  --footer-bg: #070f1e;

  width: var(--sidebar-width);
  min-height: 100vh;
  background: var(--bg);
  display: flex;
  flex-direction: column;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  z-index: 100;
}

.erp-sidebar.collapsed {
  width: var(--sidebar-collapsed);
}

/* ── Brand ── */
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: var(--brand-height);
  padding: 0 1rem;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.brand-icon {
  width: 36px;
  height: 36px;
  background: var(--accent);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #fff;
  flex-shrink: 0;
}

.brand-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-bright);
  white-space: nowrap;
  letter-spacing: 0.01em;
  transition: opacity 0.2s;
}

.collapsed .brand-text {
  opacity: 0;
  pointer-events: none;
}

/* ── Toggle ── */
.sidebar-toggle {
  position: absolute;
  top: 14px;
  right: -12px;
  width: 24px;
  height: 24px;
  background: var(--accent);
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(59,130,246,0.4);
  transition: background 0.2s;
}

.sidebar-toggle:hover {
  background: #2563eb;
}

/* ── Nav ── */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem 0;
  scrollbar-width: none;
}

.sidebar-nav::-webkit-scrollbar { display: none; }

.nav-section {
  padding: 0.25rem 0;
  margin-bottom: 0.5rem;
}

.section-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--section-label-color);
  padding: 0.5rem 1.25rem 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  transition: opacity 0.2s;
}

.collapsed .section-label {
  opacity: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 1.25rem;
  color: var(--text);
  text-decoration: none;
  border-radius: 0;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  position: relative;
}

.nav-link i {
  font-size: 1rem;
  flex-shrink: 0;
  width: 20px;
  text-align: center;
  color: var(--text);
  transition: color 0.15s;
}

.nav-link:hover {
  background: var(--bg-hover);
  color: var(--text-bright);
}

.nav-link:hover i {
  color: var(--text-bright);
}

.nav-link.active {
  background: var(--bg-active);
  color: #fff;
}

.nav-link.active i {
  color: #fff;
}

.nav-link.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #93c5fd;
  border-radius: 0 3px 3px 0;
}

.nav-text {
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  transition: opacity 0.2s;
}

.collapsed .nav-text {
  opacity: 0;
  width: 0;
}

/* ── Footer ── */
.sidebar-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: var(--footer-bg);
  border-top: 1px solid var(--border);
  flex-shrink: 0;
  overflow: hidden;
}

.user-avatar {
  width: 34px;
  height: 34px;
  background: #1e3a5f;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  font-size: 1rem;
  flex-shrink: 0;
}

.user-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: opacity 0.2s;
}

.collapsed .user-info {
  opacity: 0;
}

.user-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-bright);
  white-space: nowrap;
}

.user-role {
  font-size: 0.7rem;
  color: var(--section-label-color);
  white-space: nowrap;
}

/* ── Tooltip on collapsed ── */
.collapsed .nav-link {
  justify-content: center;
  padding: 0.55rem;
}

.collapsed .nav-link i {
  margin: 0 auto;
}

/* ── Mobile ── */
@media (max-width: 767px) {
  .erp-sidebar {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 1050;
    transform: translateX(-100%);
    transition: transform 0.25s ease;
  }

  .erp-sidebar.mobile-open {
    transform: translateX(0);
  }
}
</style>